import { FormField, MultiCheckbox } from "@rothoblaas/design-system";
import { FC, useContext, useEffect } from "react";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { usePermissionsApi } from "../../http/usePermissions";
import { useRolesApi } from "../../http/useRoles";
import styles from "./RolesPermissionsFields.module.css";

type RolesPermissionsFieldsProps = {
  roles: string[];
  permissions: string[];
  onRolesChange: (roles: string[]) => void;
  onPermissionsChange: (permissions: string[]) => void;
  disabled?: boolean;
};

// Every role and every permission as checkboxes: the permissions of the checked roles are checked and locked.
const RolesPermissionsFields: FC<RolesPermissionsFieldsProps> = ({
  roles,
  permissions,
  onRolesChange,
  onPermissionsChange,
  disabled,
}) => {
  const auth = useAuthContext();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const rolesApi = useRolesApi();
  const permissionsApi = usePermissionsApi();

  const rolesSwr = rolesApi.getAdminRoles();
  const permissionsSwr = permissionsApi.getAdminPermissions();

  useEffect(() => {
    if (auth.permissions.admin.canManageUsers) {
      rolesSwr.trigger();
      permissionsSwr.trigger();
    }
  }, [auth.permissions.admin.canManageUsers]);

  useEffect(() => {
    rolesSwr.error && dispatch(showErrorNotification(i18n, rolesSwr.error));
  }, [rolesSwr.error]);

  useEffect(() => {
    permissionsSwr.error && dispatch(showErrorNotification(i18n, permissionsSwr.error));
  }, [permissionsSwr.error]);

  const allRoles =
    rolesSwr.data?.map((x) => ({
      id: x.value,
      title: x.value,
      permissions: x.permissions,
    })) ?? [];

  // every permission, also the ones no role grants
  const allPermissions = permissionsSwr.data?.items.map((x) => ({ id: x.name, title: x.name })) ?? [];

  return (
    <div className={styles.fieldsLayout}>
      <div className={styles.sideFields}>
        <FormField label={i18n.translation.roles.plural}>
          <MultiCheckbox
            className={styles.checkboxList}
            values={allRoles.map((x) => ({
              ...x,
              selected: roles.includes(x.id),
            }))}
            disabled={disabled}
            onUpdate={(id, checked) => {
              onRolesChange(checked ? [...roles, id] : roles.filter((x) => x !== id));
            }}
          />
        </FormField>
      </div>
      <div className={styles.sideFields}>
        <FormField label={i18n.translation.permissions.plural}>
          <MultiCheckbox
            className={styles.checkboxList}
            values={(() => {
              const ps = allPermissions.map((x) => {
                const is = !!allRoles
                  .filter((r) => roles.includes(r.id))
                  .flatMap((r) => r.permissions)
                  .filter((p) => p === x.id).length;

                return {
                  ...x,
                  selected: permissions.includes(x.id) || is,
                  disabled: is,
                };
              });
              const oneSel = !!ps.find((x) => x.selected);
              return ps.map((x) => ({
                ...x,
                selected: x.selected || (x.title === "access" && oneSel),
                disabled: x.disabled || (x.title === "access" && oneSel),
              }));
            })()}
            disabled={disabled}
            onUpdate={(id, checked) => {
              onPermissionsChange(checked ? [...permissions, id] : permissions.filter((x) => x !== id));
            }}
          />
        </FormField>
      </div>
    </div>
  );
};

export default RolesPermissionsFields;
